import { LineChart } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Dialog, LoadingState } from '@/components/ui';
import type { PatientRecord } from '@/features/encounters/api';
import { useLabTrends } from '@/features/orders/api';
import {
  RESULT_FLAG_LABEL,
  REVIEW_LABEL,
  type LabResultFlag,
} from '@/features/orders/types';
import styles from './patients.module.css';

/**
 * Full record "Results": every result with the same per-test state as the
 * lab inbox and the doctor's review, outside lab reports, and trends (LAB-06).
 */
export function RecordResults({ rec }: { rec: PatientRecord }) {
  const [trends, setTrends] = useState(false);
  const list = !rec.clinical ? (
    <p className={styles.note}>Results are shown to clinical roles only.</p>
  ) : rec.results.length === 0 ? (
    <p className={styles.note}>No results yet.</p>
  ) : (
    <ul className={styles.visitList}>
      {rec.results.map((r) => (
        <li key={r.labOrderId}>
          <div>
            <p>
              <strong>{r.number}</strong> ·{' '}
              {r.status === 'reviewed'
                ? `reviewed${r.reviewedBy ? ` by ${r.reviewedBy.fullName}` : ''}`
                : 'waiting for the doctor'}
            </p>
            {r.items.map((i) => (
              <p key={i.id ?? i.testName} className={styles.sub}>
                {i.testName}: {i.resultValue ?? '—'}
                {i.resultUnit ? ` ${i.resultUnit}` : ''}
                {i.referenceRange ? ` (ref ${i.referenceRange})` : ''}{' '}
                {i.resultFlag && (
                  <Badge
                    tone={i.resultFlag === 'normal' ? 'success' : 'warning'}
                  >
                    {RESULT_FLAG_LABEL[i.resultFlag as LabResultFlag]}
                  </Badge>
                )}{' '}
                {i.review && (
                  <Badge
                    tone={
                      i.review === 'released'
                        ? 'success'
                        : i.review === 'held'
                          ? 'danger'
                          : 'info'
                    }
                  >
                    {REVIEW_LABEL[i.review]}
                  </Badge>
                )}
                {i.doctorComment ? ` · “${i.doctorComment}”` : ''}
              </p>
            ))}
            {r.reviewComment && (
              <p className={styles.sub}>“{r.reviewComment}”</p>
            )}
          </div>
          <Link to={`/consultations/${r.encounterId}`}>Encounter</Link>
        </li>
      ))}
    </ul>
  );
  if (!rec.clinical) return list;
  const outside = rec.outsideReports ?? [];
  return (
    <div className={styles.stack}>
      <div>
        <Button
          size="sm"
          icon={<LineChart size={14} />}
          onClick={() => setTrends(true)}
        >
          Trends
        </Button>
      </div>
      {list}
      {outside.length > 0 && (
        <>
          <p className="eyebrow">Outside lab reports</p>
          <ul className={styles.visitList}>
            {outside.map((r) => (
              <li key={r.id}>
                <div>
                  <p>
                    <strong>{r.source ?? 'Outside lab'}</strong>
                    {r.reportDate ? ` · ${r.reportDate}` : ''} · checked by{' '}
                    {r.enteredBy.fullName}
                  </p>
                  {r.results.map((x) => (
                    <p key={x.test} className={styles.sub}>
                      {x.test}: {x.value}
                      {x.unit ? ` ${x.unit}` : ''}
                      {x.referenceRange
                        ? ` (ref ${x.referenceRange})`
                        : ''}{' '}
                      {x.flag && x.flag !== 'normal' && (
                        <Badge tone="warning">
                          {RESULT_FLAG_LABEL[x.flag as LabResultFlag]}
                        </Badge>
                      )}
                    </p>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {trends && (
        <TrendsDialog
          patientId={rec.patient.id}
          onClose={() => setTrends(false)}
        />
      )}
    </div>
  );
}

function TrendsDialog({
  patientId,
  onClose,
}: {
  patientId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useLabTrends(patientId);
  return (
    <Dialog open wide title="Result trends" onClose={onClose}>
      {isLoading ? (
        <LoadingState />
      ) : !data?.length ? (
        <p className={styles.note}>No results yet.</p>
      ) : (
        <div className={styles.stack}>
          {data.map((t) => (
            <div key={t.test}>
              <p>
                <strong>{t.test}</strong>
              </p>
              <table className={styles.compare}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Value</th>
                    <th>Range</th>
                    <th>Flag</th>
                    <th>Doctor&apos;s note</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {t.points.map((p, i) => (
                    <tr key={i}>
                      <td>{new Date(p.at).toLocaleDateString('en-GB')}</td>
                      <td>
                        {p.value}
                        {p.unit ? ` ${p.unit}` : ''}
                      </td>
                      <td>{p.referenceRange ?? '—'}</td>
                      <td>
                        {p.flag ? RESULT_FLAG_LABEL[p.flag] : '—'}
                        {p.review && p.review !== 'released'
                          ? ` · ${REVIEW_LABEL[p.review]}`
                          : ''}
                      </td>
                      <td>{p.doctorComment ?? '—'}</td>
                      <td>
                        {p.source === 'outside' ? 'Outside lab' : p.orderNumber}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}
